import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Folder, Type, Info, Check } from 'lucide-react';

import BaseModal from '@/shared/components/modals/core/BaseModal.jsx';
import Input from '@/shared/components/elements/Input';
import Button from '@/shared/components/elements/Button';
import { folderSchema } from '@/feature/Dashboard/schemas/folderSchemas';
import { extractFieldErrors } from '@/shared/utils/errorUtils';

function FolderModal({
  isOpen,
  onClose,
  onSubmit,
  folder = null,
  isLoading = false,
}) {
  const [serverErrors, setServerErrors] = useState({});
  const isEditMode = !!folder;

  const form = useForm({
    defaultValues: {
      name: folder?.name || '',
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        await onSubmit({ ...value, folder });
        form.reset();
        onClose();
      } catch (error) {
        console.error('Folder operation error:', error);
        const fieldErrors = extractFieldErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          setServerErrors(fieldErrors);
        }
      }
    },
  });

  useEffect(() => {
    if (isOpen && folder) {
      form.setFieldValue('name', folder.name);
    } else if (isOpen && !folder) {
      form.reset();
      setServerErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, folder]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'ویرایش فولدر' : 'ایجاد فولدر جدید'}
      icon={<Folder size={20} />}
      iconVariant="primary"
      size="md"
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            icon={<Check size={18} />}
            iconPosition="left"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {isEditMode ? 'ذخیره تغییرات' : 'ایجاد فولدر'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-5 p-6"
      >
        <form.Field
          name="name"
          validators={{
            onChange: folderSchema.shape.name,
          }}
        >
          {(field) => {
            const fieldError = field.state.meta.errors?.[0];
            const errorMessage = fieldError
              ? typeof fieldError === 'string'
                ? fieldError
                : fieldError.message || 'خطای اعتبارسنجی'
              : serverErrors.name;

            return (
              <Input
                label="نام فولدر"
                type="text"
                placeholder="نام فولدر را وارد کنید..."
                value={field.state.value}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  if (serverErrors.name) {
                    setServerErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }
                }}
                error={errorMessage}
                icon={<Type size={18} />}
                disabled={isLoading}
                maxLength={100}
              />
            );
          }}
        </form.Field>

        {!isEditMode && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 backdrop-blur-sm dark:border-blue-500/30 dark:bg-blue-500/10">
            <div className="flex items-start gap-2">
              <Info
                size={16}
                className="mt-0.5 text-blue-600 dark:text-blue-400"
              />
              <p className="text-xs leading-relaxed text-blue-700 dark:text-blue-300">
                فولدر جدید ایجاد خواهد شد و می‌توانید داشبوردهای خود را در آن
                سازماندهی کنید.
              </p>
            </div>
          </div>
        )}
      </form>
    </BaseModal>
  );
}

FolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  folder: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default FolderModal;
